/**
 * One way to call the API and one way to show money.
 *
 * Every page used to define its own `API` constant (some with `/api`, some
 * without), build its own auth header, call `r.json()` without a catch (an
 * Emergent 520 page is HTML, so the screen showed "Unexpected token <") and
 * format money its own way: six copies, some giving "$12500.00" and others
 * "$12,500.00". New and touched code uses these.
 */
import { apiErrorMessage } from './apiError';

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * fetchJson('/insurance/selletrans/claims', { token })
 * fetchJson('/insurance/selletrans/claims', { token, method: 'POST', body: {...} })
 *
 * Returns the parsed body. On an error status it throws an Error whose message
 * is readable (apiErrorMessage) and carries `status` and `body`.
 */
export async function fetchJson(path, { token, method = 'GET', body, headers = {}, signal } = {}) {
  const r = await fetch(`${API}${path}`, {
    method,
    signal,
    headers: {
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...headers,
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const data = await r.json().catch(() => null);
  if (!r.ok) {
    const err = new Error(apiErrorMessage(data, `The request failed (${r.status}).`));
    err.status = r.status;
    err.body = data;
    throw err;
  }
  return data;
}

const USD = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

/** "$12,500.00". Missing or non-numeric values show as "—": a made-up $0.00
 * hid that the amount was missing (code review of 26 Oct, E.13). */
export const formatMoney = (n) => (
  n === null || n === undefined || n === '' || !Number.isFinite(Number(n)) ? '—' : USD.format(Number(n))
);
